import React, { useCallback, useState, useEffect } from "react";
import type { ErrorObject } from "serialize-error";
import { useSearchParams, Link, useNavigate } from "react-router";
import {
  Box,
  Button,
  Alert,
  AlertTitle,
  AlertDescription,
  Text,
  Icon,
  BrandIcon,
  Stack,
  Badge,
} from "@gusarov-studio/rubik-ui";
import { Logo } from "../../../common/components/Logo";
import { CenteredColumnLayout } from "../../../common/components/Layout";
import { HeroBackground } from "../../../common/components/HeroBackground";
import { MicrosoftIcon } from "../components/MicrosoftIcon";
import { routesMap } from "../../../router/routesMap";
import type { ErrorInfo } from "../types";
import { processRtkError } from "../../../common/utils/processRtkError";
import { supabase } from "../../../supabase";
import { useWorkspaceData } from "../../workspaces";

function SignupScreen() {
  const { isAuthenticated, user } = useWorkspaceData();
  const [serverError, setServerError] = useState<ErrorInfo | null>(null);
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const continueWithGoogle = useCallback(async () => {
    supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        queryParams: { hd: "*" }, // Filter signup to Google work accounts by default
        redirectTo: `${window.location.origin + routesMap.login.url()}?provider=google`,
      },
    });
  }, []);

  // Extract OAuth redirect parameters
  const provider = searchParams.get("provider");
  const errorName = searchParams.get("error");
  const errorCode = searchParams.get("error_code");
  const errorDescription = searchParams.get("error_description");

  // Handle OAuth error on redirect from Google
  useEffect(() => {
    if (provider !== "google") return;
    if (!errorName || !errorCode || !errorDescription) return;

    const authError: ErrorObject = {
      name: errorName,
      code: errorCode,
      message: errorDescription,
    };

    const errorInfo = processRtkError(authError);
    setServerError(errorInfo);
  }, [setServerError, provider, errorName, errorCode, errorDescription]);

  // Handle successful OAuth on redirect from Google
  useEffect(() => {
    if (provider !== "google") return;
    if (!isAuthenticated || !user) return;
    // Redirect to onboarding if user hasn't completed it yet
    if (user.signup_completed) {
      navigate(routesMap.newFile.url());
    } else {
      navigate(routesMap.onboarding.url());
    }
  }, [provider, isAuthenticated, user, navigate]);

  return (
    <HeroBackground
      className="h-full"
      variant="gray-100"
    >
      <CenteredColumnLayout className="px-2">
        <Stack
          className="mb-6"
          direction="row"
          spacing={6}
          alignItems="center"
          justifyContent="center"
        >
          <Logo />
          <Text
            as="div"
            size={18}
            weight="600"
          >
            LangFast
          </Text>
          <Badge
            className="relative top-[1px]"
            appearance="subtle"
            variant="white"
            size="16"
          >
            Beta
          </Badge>
        </Stack>
        <Box
          padding="32px 24px 24px"
          borderWidth={1}
          variant="primary"
          radius={16}
        >
          <Text
            align="center"
            block
            size="24"
            weight="600"
          >
            Create your account
          </Text>
          <Text
            className="mt-1.5!"
            align="center"
            block
            size="14"
            color="muted"
          >
            Use your Google or Microsoft work account
          </Text>

          {serverError && (
            <Box marginTop={16}>
              <Alert
                variant="danger"
                icon={
                  <Icon
                    glyph="alert-circle"
                    color="inherit"
                    size="20"
                  />
                }
              >
                <AlertTitle>{serverError.title}</AlertTitle>
                <AlertDescription>{serverError.description}</AlertDescription>
              </Alert>
            </Box>
          )}

          <Box marginTop={24}>
            <Stack
              spacing="12px"
              direction="column"
              alignItems="stretch"
            >
              <Button
                widthMode="fill"
                appearance="outline"
                variant="secondary"
                size="48"
                onClick={continueWithGoogle}
              >
                <BrandIcon
                  size={20}
                  glyph="google"
                />
                <span className="pl-1">Continue with Google work account</span>
              </Button>
              <Button
                widthMode="fill"
                appearance="outline"
                variant="secondary"
                size="48"
                disabled
                onClick={() => {}}
              >
                <MicrosoftIcon size={20} />
                <span className="pl-1">Continue with Microsoft work account</span>
              </Button>
            </Stack>
          </Box>

          <Text
            className="mt-4!"
            size="14"
            align="center"
            color="primary"
            as="div"
          >
            Already have an account? <Link to={routesMap.login.url()}>Log in</Link>
          </Text>
        </Box>
        <Text
          className="mt-6! mx-auto! w-[240px]"
          size="12"
          align="center"
          color="muted"
          as="div"
        >
          By clicking continue, you agree to our{" "}
          <Link to={routesMap.terms.url()}>Terms of Service</Link> and{" "}
          <Link to={routesMap.privacy.url()}>Privacy Policy</Link>.
        </Text>
      </CenteredColumnLayout>
    </HeroBackground>
  );
}

export { SignupScreen };
